<template>
  <div class="flex flex-col h-full">
    <Alert v-if="!loadingAccount && errorAccount" class="mx-4 mt-2 w-1/2" type="error">{{ errorAccount }}</Alert>

    <div class="progress text-center" v-if="loadingAccount">
      <Spinner size="lg" class="text-primary" />
    </div>

    <div v-else id="account-header" class="inner">
      <div class="flex place-content-between gap-5 items-center">
        <div>
          <div class="flex content-between gap-3 items-center mb-1">
            <h1 class="truncate max-w-lg">{{ account.displayName }}</h1>

            <Chip
              v-if="account.accountStatus === 'P'"
              label="prospect"
              variant="outline"
              size="sm"
              color-class="border-pale-blue text-capital-blue"
              data-test="prospect-chip"
            />
            <template v-if="!isAClientAccount">
              <Badge
                v-for="agency in accountAgencies"
                :key="agency"
                variant="outline"
                class="bg-none font-normal"
                :class="badgeStyleForAgency(agency)"
                data-test="cac-entity-chip">
                {{ agency }}
              </Badge>
            </template>
            <FavoriteIndicator v-if="!isAClientAccount" v-model="account.favorite" @update:model-value="toggleFavorite(account)" :loading="favoriteProgress" class="text-center text-primary" />
            <div class="tooltip tooltip-bottom flex items-center" data-tip="Edit Account">
              <Pencil v-if="!isAClientAccount" :size="16" class="stroke-primary cursor-pointer"
                      @click="handleAccountEdit(account.accountID)"
              />
            </div>
            <SalesforceLink v-if="!isAClientAccount" :sales-force-id="account.salesForceID" />
            <div v-if="IsUserAnAdmin(Store.state.user) || IsUserABroker(Store.state.user)" class="tooltip tooltip-bottom flex items-center" data-tip="View as Client">
              <ViewIcon :size="16" class="cursor-pointer stroke-capital-blue" @click="handleViewAsClient(account.accountID)" />
            </div>

            <AccountWarning v-if="!isAClientAccount" :account="account" />
          </div>

          <a v-if="showDomain && !isAClientAccount" :href="`//${account.domainName}`" target="_blank" rel="noopener noreferrer" class="lowercase">{{ account.domainName }}</a>
          <span v-if="showDomain && !isAClientAccount" class="text-md text-gray-500"> &nbsp; </span>
          <span v-if="!isAClientAccount" class="text-gray-500 uppercase text-xs"> {{ account.topLevelIndustry }}</span>

          <div v-if="account.parentCompany" class="uppercase text-gray-500 text-xs">
            <strong>Parent Account:&nbsp;&nbsp;</strong>
            <template v-if="canViewParentAccount(account.parentAccountID)">
              <router-link :to="`/parent/account/${account.parentAccountID}/overview`" data-test="parent-company">{{
                account.parentCompany
              }}</router-link>
            </template>
            <span v-else>{{ account.parentCompany }}</span>
          </div>
        </div>

        <div v-if="!isAClientAccount" class="text-right statistics">
          <Skeleton v-if="loadingKPI && !errorKPI" shape="rectangle" width="100%" height="80px" class="rounded-md" />
          <Alert v-if="!loadingKPI && errorKPI" type="error">{{ errorKPI }}</Alert>
          <Statistics v-if="!loadingKPI && !errorKPI && !isAClientAccount" dense no-borders :statistics="statistics" />
        </div>
      </div>
    </div>

    <div class="w-full">
      <Tabs
        id="account-tabs"
        :model-value="currentTab"
        :items="accountTabItems"
        class="tab-bar uppercase w-full"
        variant="lifted"
        @update:model-value="onAccountTabChange" />
    </div>

    <div
      v-if="!loadingTabs && accountTabs.length === 0"
      class="alert p-4 w-xl mx-auto mt-8"
    >
      <TriangleAlert size="18"/>
      <span>
        Insufficient permissions. Contact <a href="mailto:platform@cacgroup.com">platform@cacgroup.com</a>
        for assistance.
      </span>
    </div>

    <router-view v-if="!loadingAccount && accountTabs.length > 0" :account="account"></router-view>
  </div>
</template>

<script>
import { accountService, userAccountService } from '@/services/api';
import Statistics from '@/components/Statistics';
import { ErrorLoadingKPI, FailedToLoadPage } from '@/utilities/Errors';
import { DateTime } from 'luxon';
import { compactMoney } from '@/utilities/Money';
import { compactNumber } from '@/utilities/Number';
import FavoriteIndicator from '@/components/FavoriteIndicator';
import { Events } from '@/store/EventBus';
import AccountWarning from '@/components/AccountWarning';
import SalesforceLink from '@/components/SalesforceLink.vue';
import {
  IsUserABroker,
  IsUserAClient,
  IsUserAnAdmin,
  GetAllowedParentAccountIds,
} from '@/utilities/AuthUtils';
import { AccountTabPermissionOrder } from '@/types/UserPermissions';
import { Commit, Store } from '@/store';
import Alert from '@/components/v2/Alert.vue';
import Badge from '@/components/ui/Badge.vue';
import Chip from '@/components/v2/Chip.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import Spinner from '@/components/v2/Spinner.vue';
import Tabs from '@/components/v2/Tabs.vue';
import { Pencil, View as ViewIcon, TriangleAlert } from '@lucide/vue';
import { StoreMutationType } from '@/types/StoreMutationType';
import { registerResizeItem } from '@/utilities/resize';
import { useEntityTabs } from '@/composables/useEntityTabs';

export default {
  name: 'AccountView',
  components: { Alert, Badge, Chip, Statistics, FavoriteIndicator, AccountWarning, SalesforceLink, Skeleton, Spinner, Tabs, ViewIcon, Pencil, TriangleAlert },
  setup() {
    const { getTabs } = useEntityTabs();

    return {
      getTabs,
    };
  },
  data: () => ({
    loadingAccount: true,
    errorAccount: undefined,
    loadingKPI: true,
    errorKPI: undefined,
    account: {},
    statistics: [],
    favoriteProgress: false,
    accountTabs: [],
    loadingTabs: true,
  }),
  computed: {
    Store() {
      return Store;
    },
    isProspect() {
      return this.account?.accountStatus === 'P';
    },
    showDomain() {
      if (this.account.noDomain === true) {
        return false;
      }
      return this.account.domainName;
    },
    isAClientAccount() {
      return IsUserAClient(Store.state.user);
    },
    accountAgencies() {
      if (!this.account.agencies) return [];
      return this.account.agencies.split(',').filter((a) => a.trim() !== '');
    },
    currentTab() {
      return this.$route.path.split('/')[3];
    },
    accountTabItems() {
      return this.accountTabs.map((t) => ({ value: t.key, label: t.label }));
    },
  },
  methods: {
    onAccountTabChange(val) {
      const tab = this.accountTabs.find((t) => t.key === val);
      if (tab) this.$router.push(tab.route);
    },
    IsUserAnAdmin,
    IsUserABroker,
    badgeStyleForAgency(agency) {
      switch (agency.trim()) {
        case 'Specialty':
          return 'text-specialty-teal/90 border-specialty-teal/80';
        case 'Agency':
          return 'text-agency-red/90 border-agency-red/80';
        case 'Capital':
          return 'text-capital-blue/90 border-capital-blue/80';
        default:
          return '';
      }
    },
    async toggleFavorite(account) {
      this.favoriteProgress = true;
      if (!account.favorite) {
        await userAccountService.remove(this.account.accountID);
        this.account.favorite = false;
      } else {
        await userAccountService.create(this.account.accountID);
        this.account.favorite = true;
      }
      this.favoriteProgress = false;
    },
    async loadAccount() {
      try {
        this.loadingAccount = true;
        this.account = await accountService.findById(this.$route.params.id);
        if (!this.account.displayName) this.account.displayName = this.account.accountName;
        return true;
      } catch (err) {
        if ('response' in err && err.response.status === 404) {
          return this.$router.replace('/');
        }
        console.error(err);
        this.errorAccount = FailedToLoadPage;
        return false;
      } finally {
        this.loadingAccount = false;
      }
    },
    async refreshKPI() {
      try {
        this.loadingKPI = true;
        //if user, return early or else kpi will fail w/ 404
        if (IsUserAClient(Store.state.user)) {
          this.loadingKPI = false;
          return;
        }
        const kpi = await accountService.kpi(this.$route.params.id);
        let clientSince = DateTime.fromISO(kpi.clientSince).toLocaleString(DateTime.DATE_SHORT);
        if (clientSince.includes('Invalid')) {
          clientSince = '--';
        }

        this.statistics = [
          {
            key: 'client-since',
            value: clientSince,
            heading: this.isProspect ? 'Target Since' : 'Client Since',
            loading: false,
            classes: ['dense', 'd-lg-block'],
          },
          {
            key: 'current-premium',
            value: compactMoney(this.isProspect ? kpi.externalPremium : kpi.currentPremium),
            heading: this.isProspect ? 'External Premium' : 'Current Premium',
            loading: false,
            classes: ['dense'],
            toolTip: 'Includes BOR Premium',
          },
          {
            key: 'policy-count',
            value: compactNumber(this.isProspect ? kpi.externalPolicies : kpi.currentPolicies),
            heading: this.isProspect ? 'External Policies' : 'Current Policies',
            loading: false,
            classes: ['dense'],
          },
        ];
      } catch (err) {
        console.error(err);
        this.errorKPI = ErrorLoadingKPI;
      } finally {
        this.loadingKPI = false;
      }
    },
    handleAccountEdit(accountID) {
      this.$router.push(`/account/${accountID}/edit`);
    },
    async handleViewAsClient(accountID) {
      Commit(StoreMutationType.ENABLE_CLIENT_VIEW_MODE, {
        id: -1,
        name: '',
        allowedAccountIds: [accountID],
      });

      await this.loadAccount();
      await this.refreshKPI();
    },
    canViewParentAccount(accountId) {
      return !this.isAClientAccount || GetAllowedParentAccountIds(Store.state.user).includes(accountId);
    },
    async loadAccountTabs() {
      this.loadingTabs = true;
      try {
        this.accountTabs = await this.getTabs('account', this.account.accountID.toString());
      } catch (error) {
        console.error('Error loading account tabs:', error);
        this.accountTabs = [];
      } finally {
        this.loadingTabs = false;
      }
    },
  },
  watch: {
    async isAClientAccount() {
      // when client view is exited, fetch KPIs and display
      await this.loadAccount();
      await this.refreshKPI();
      await this.loadAccountTabs();
    },
    accountTabs() {
      if (!this.accountTabs.map((t) => t.key).includes(this.currentTab)) {
        if (this.accountTabs.length > 0) {
          this.$router.push(this.accountTabs[0].route);
        }
      }
    }
  },

  // -----------
  // lifecycles
  // -----------
  async created() {
    const loaded = await this.loadAccount();
    if (loaded) {
      await this.refreshKPI();
      await this.loadAccountTabs();
      addEventListener(Events.REFRESH_ACCOUNT_KPI, this.refreshKPI);
    }
  },
  mounted() {
    // Register resizable items
    registerResizeItem({ name: 'account-header', selector: 'account-header' });
    registerResizeItem({ name: 'account-tabs', selector: 'account-tabs' });

    // Determine the landing tab based on permissions
    const user = Store.state.user;
    const initialPath = this.$route.path.split('/').slice(3).join('/') || 'overview';
    const initialTab = initialPath.split('/')[0];
    let landingPath = initialPath;

    // TODO app-role: if we migrate all roles to db roles, this should be applied to all roles
    if (IsUserAClient(user)) {
      // If the current top-level tab is restricted, redirect to the first accessible tab
      const currentTabPerm = AccountTabPermissionOrder.find((tabPerm) => tabPerm.tab === initialTab);
      const hasAccessToCurrentTab = currentTabPerm && user?.permissions.includes(currentTabPerm.permission);
      if (!hasAccessToCurrentTab) {
        landingPath =
          AccountTabPermissionOrder.find((tabPerm) => user?.permissions.includes(tabPerm.permission))?.tab ||
          initialPath;
      }
    }

    const url = `/account/${this.$route.params.id}${landingPath ? '/' + landingPath : ''}`;
    const navigationOptions = {
      path: url,
      ...(landingPath === initialPath && { query: this.$route.query }), // Preserve query only if the tab hasn't changed
    };

    this.$router.push(navigationOptions);
  },
  beforeUnmount() {
    removeEventListener(Events.REFRESH_ACCOUNT_KPI, this.refreshKPI);
  },
};
</script>

<style scoped>
.progress {
  display: grid;
  align-content: center;
  align-items: center;
  justify-content: center;
  justify-items: center;
  height: 10rem;
}
</style>
